/**
 * Capacitor deep link handling
 *
 * Two kinds of inbound URL reach this handler via `appUrlOpen`:
 *
 * 1. OAuth callbacks via the custom scheme `ai.panluma.app://`. The OAuth flow
 *    uses it as the redirect URI: Chrome Custom Tabs intercepts a custom scheme
 *    immediately (unlike https:// URLs, which it tries to load in-browser first)
 *    and routes it back to the app.
 *
 * 2. Universal Links (iOS) / App Links (Android) for entity links in the
 *    reminder & notification emails we send — e.g. tapping
 *    `https://app.panluma.ai/sales/deals/{id}` in Mail opens the installed app
 *    on that page instead of the browser. The OS only delivers these for our
 *    verified domain (apple-app-site-association / assetlinks.json under
 *    `/.well-known/`). We resolve them to the in-app SPA route by stripping the
 *    origin and navigating to the path + query + hash. See docs/platform/mobile-app.md.
 *
 * Email entity links use the canonical, OS-verified host `app.panluma.ai` and
 * carry the tenant's own host as a `?domain=` hint (see backend
 * `email_delivery._append_deep_link_domain`). That hint is a WEB-redirect
 * signal only — the native app's API base is bound separately (first-run
 * WorkspaceSelector + the LOGIN_ON_CUSTOM_DOMAIN bounce), and `API_BASE_URL` is
 * fixed at module load, so we strip `domain` from the in-app route rather than
 * re-binding mid-session.
 */

import { App, URLOpenListenerEvent } from '@capacitor/app';
import { isCapacitor } from '../api/config';

let isInitialized = false;

/** Hosts whose https links we treat as in-app universal/app links. */
const APP_LINK_HOST_SUFFIX = '.panluma.ai';
const APP_LINK_HOST = 'app.panluma.ai';

function isAppLinkHost(hostname: string): boolean {
  return hostname === APP_LINK_HOST || hostname.endsWith(APP_LINK_HOST_SUFFIX);
}

/**
 * Drop the `domain=` workspace hint from a deep-link query string (it's a
 * web-redirect signal, not part of the in-app route), preserving every other
 * param (e.g. `tab=messages`). Returns a leading-`?` query string, or ''.
 */
function stripDomainParam(search: string): string {
  const params = new URLSearchParams(search);
  params.delete('domain');
  const rest = params.toString();
  return rest ? `?${rest}` : '';
}

/**
 * Map an inbound deep-link URL to the in-app SPA path to navigate to, or null
 * if it isn't a link we own. Exported for testing.
 */
export function resolveDeepLinkPath(rawUrl: string): string | null {
  let url: URL;
  try {
    url = new URL(rawUrl);
  } catch {
    return null;
  }

  // OAuth callback via custom scheme deep link.
  // Format: ai.panluma.app://auth/sso/callback?code=...&state=...
  // new URL() parses this as protocol=ai.panluma.app:, hostname=auth, pathname=/sso/callback
  if (url.protocol === 'ai.panluma.app:') {
    return `/auth${url.pathname}${url.search}`;
  }

  // Universal Link / App Link to an entity page (from email CTAs etc.).
  // Navigate to the relative SPA route — never cross-origin (the WebView stays
  // on its bundled localhost origin).
  if (url.protocol === 'https:' && isAppLinkHost(url.hostname)) {
    return `${url.pathname}${stripDomainParam(url.search)}${url.hash}`;
  }

  return null;
}

/**
 * Initialize deep link listener for Capacitor apps
 * Should be called once when the app starts
 */
export function initDeepLinkListener(navigate: (path: string) => void): void {
  if (!isCapacitor || isInitialized) {
    return;
  }

  isInitialized = true;

  App.addListener('appUrlOpen', (event: URLOpenListenerEvent) => {
    const path = resolveDeepLinkPath(event.url);
    if (path) {
      // Log the route only (no query string) — deep-link URLs can carry OAuth
      // codes / reset tokens we must not leak to the device console.
      console.log('[DeepLink] Navigating in-app to:', path.split('?')[0]);
      navigate(path);
    }
  });

  console.log('[DeepLink] Listener initialized');
}

/**
 * Close the in-app browser after OAuth callback
 */
export async function closeInAppBrowser(): Promise<void> {
  if (!isCapacitor) return;

  try {
    const { Browser } = await import('@capacitor/browser');
    await Browser.close();
  } catch (error) {
    // Browser might not be open, ignore
    console.log('[DeepLink] Browser close:', error);
  }
}
